import {
  animate,
  AUTO_STYLE,
  state,
  style,
  transition,
  trigger
} from '@angular/animations';
import { Component, ElementRef, Renderer2, ViewChild, signal, output, input, OnDestroy, OnInit, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl } from '@angular/forms';
import { DropdownItem } from 'src/app/core/models/dropdown.model';

@Component({
  standalone: true,
  selector: 'sort-dropdown',
  templateUrl: './sort-dropdown.component.html',
  styleUrls: ['./sort-dropdown.component.scss'],
  animations: [
    trigger('collapse', [
      state('false', style({ height: AUTO_STYLE, visibility: AUTO_STYLE })),
      state('true', style({ height: '0', visibility: 'hidden' })),
      transition('false => true', animate(300 + 'ms ease-in')),
      transition('true => false', animate(300 + 'ms ease-out'))
    ])
  ],
  imports: [CommonModule]
})
export class SortDropdownComponent implements OnInit, OnDestroy {
  private renderer = inject(Renderer2);
  // private cdr = inject(ChangeDetectorRef);

  private _selectedOption = signal<DropdownItem<string> | null>(null);

  readonly selectedOption = this._selectedOption.asReadonly();
  readonly isExpanded = computed(() => !this.collapsed);
  readonly hasOptions = computed(() => this.list.length > 0);



  readonly control = input<FormControl<string | null>>(new FormControl<string | null>(''));
  readonly message = input<string>("");
  readonly placeholder = input<string>('');
  readonly multiple = input<boolean>(false);
  readonly value = input<string>("");
  readonly required = input<boolean>(false);
  readonly label = input<string>("");
  readonly errorMessages = input<any>(null);
  readonly focused = input<boolean>(false);
  readonly width = input<string | number>('100%');
  readonly defaultOption = input<DropdownItem<string> | null>(null);

  // Update outputs to use Angular 20 output() function
  valueChanged = output<any>();
  openedChange = output<any>();

  // ViewChild
  @ViewChild('toggleButton') _collapsedOut!: ElementRef;

  // Private cleanup function
  private cleanupListener?: () => void;
  private controlValueSubscription?: any;




  readonly listInput = input<DropdownItem<string>[]>([]);
  readonly collapsedInput = input<boolean>(true);

  private _list = signal<DropdownItem<string>[]>([]);
  private _collapsed = signal<boolean>(true);

  get list(): DropdownItem<string>[] {
    return this._list();
  }

  get collapsed(): boolean {
    return this._collapsed();
  }

  constructor() {
    effect(() => {
      const listValue = this.listInput();
      this._list.set(listValue || []);
      this.initializeSelectedOption();
    });

    effect(() => {
      const collapsedValue = this.collapsedInput();
      this._collapsed.set(collapsedValue);
      this.openedChange.emit(!collapsedValue);
    });
  }


  ngOnInit(): void {
    this.initializeSelectedOption();
    this.setupGlobalClickListener();
    
    // Subscribe to FormControl valueChanges to sync selectedOption
    this.controlValueSubscription = this.control().valueChanges.subscribe(value => {
      this.syncSelectedOptionFromControl(value);
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.controlValueSubscription) {
      this.controlValueSubscription.unsubscribe();
    }
    if (this.cleanupListener) {
      this.cleanupListener();
    }
  }

  toggle(): void {
    this._collapsed.set(!this._collapsed());
    // this.cdr.detectChanges();
  }

  expand(): void {
    this._collapsed.set(false);
    // this.cdr.detectChanges();
  }

  collapse(): void {
    this._collapsed.set(true);
    // this.cdr.detectChanges();
  }

  changeSelection(selection: string, i: number): void {
    if (this.list && this.list[i]) {
      this._selectedOption.set(this.list[i]!);
    }
    this.control().patchValue(selection);
    this.valueChanged.emit(selection);
  }

  private initializeSelectedOption(): void {
    if (this.list && this.list.length > 0) {
      // Check if control has a value and find matching option
      const controlValue = this.control().value;
      if (controlValue !== null && controlValue !== undefined && controlValue !== '') {
        const matchingOption = this.list.find(item => item.value === controlValue);
        if (matchingOption) {
          this._selectedOption.set(matchingOption);
          return;
        }
      }
      
      // Otherwise, select first item as default
      const firstItem = this.list[0];
      if (firstItem) {
        this._selectedOption.set(firstItem);
      }
    }
  }

  private syncSelectedOptionFromControl(value: string | null): void {
    if (value !== null && value !== undefined && value !== '') {
      const matchingOption = this.list.find(item => item.value === value);
      if (matchingOption) {
        this._selectedOption.set(matchingOption);
      }
    } else {
      // If control is empty, select first item (default)
      if (this.list && this.list.length > 0) {
        const firstItem = this.list[0];
        if (firstItem) {
          this._selectedOption.set(firstItem);
        }
      }
    }
  }

  private setupGlobalClickListener(): void {
    this.cleanupListener = this.renderer.listen('window', 'click', (e: Event) => {
      /**
       * Only run when toggleButton is not clicked
       * If we don't check this, all clicks (even on the toggle button) gets into this
       * section which in the result we might never see the menu open!
       * And the menu itself is checked here, and it's where we check just outside of
       * the menu and button the condition above must close the menu
       */
      if (this._collapsedOut?.nativeElement?.ariaExpanded === 'true') {
        this._collapsed.set(false);
      } else if (this._collapsedOut?.nativeElement?.ariaExpanded === 'false') {
        this._collapsed.set(true);
      }

      // this.cdr.detectChanges();
    });
  }
}


